import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../utils/apiUrl';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [siteId, setSiteId] = useState(localStorage.getItem('siteId') || '');

  useEffect(() => {
    const initAuth = async () => {
      // Always re-resolve the site for this domain. The cached value is only an
      // instant default — if it points at a stale/duplicate row, correct it, so
      // profiles and site name never silently disappear.
      try {
        const hostname = window.location.hostname;
        let siteRes;
        try {
          siteRes = await axios.get(`${API_URL}/api/sites/by-domain/${hostname}`);
        } catch {
          siteRes = await axios.get(`${API_URL}/api/sites/default/config`);
        }
        const newSiteId = siteRes.data.id;
        if (newSiteId && newSiteId !== siteId) {
          setSiteId(newSiteId);
          localStorage.setItem('siteId', newSiteId);
        }
      } catch (e) {
        // Fallback — will be set when user logs in or site is created
      }

      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.type === 'user') {
            setUser(response.data.user);
            setUserType('user');
          } else if (response.data.type === 'operator') {
            setUser(response.data.operator);
            setUserType('operator');
          } else if (response.data.type === 'admin') {
            setUser(response.data.admin);
            setUserType('admin');
          } else if (response.data.type === 'affiliate') {
            setUser(response.data.affiliate);
            setUserType('affiliate');
          } else if (response.data.type === 'lawyer') {
            setUser(response.data.lawyer);
            setUserType('lawyer');
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password, type = 'user') => {
    try {
      let endpoint = '/api/auth/login';
      let payload = { email, password };
      
      if (type === 'admin') {
        endpoint = '/api/auth/admin/login';
      } else if (type === 'operator') {
        endpoint = '/api/auth/operator/login';
        payload = { username: email, password };
      } else if (type === 'affiliate') {
        endpoint = '/api/auth/affiliate/login';
      } else if (type === 'lawyer') {
        endpoint = '/api/auth/lawyer/login';
      } else {
        payload.site_id = siteId;
      }

      const response = await axios.post(`${API_URL}${endpoint}`, payload);
      const { token: newToken, user: userData, admin, operator, affiliate, lawyer } = response.data;
      
      setToken(newToken);
      localStorage.setItem('token', newToken);
      
      if (type === 'admin') {
        setUser(admin);
        setUserType('admin');
      } else if (type === 'operator') {
        setUser(operator);
        setUserType('operator');
      } else if (type === 'affiliate') {
        setUser(affiliate);
        setUserType('affiliate');
      } else if (type === 'lawyer') {
        setUser(lawyer);
        setUserType('lawyer');
      } else {
        setUser(userData);
        setUserType('user');
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (email, password, username, gender, lookingFor, referralCode, city, age, aboutMe) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        email,
        password,
        username,
        site_id: siteId,
        gender: gender || null,
        looking_for: lookingFor || null,
        referral_code: referralCode || null,
        city: city || null,
        age: age || null,
        about_me: aboutMe || null
      });
      
      const { token: newToken, user: userData } = response.data;
      setToken(newToken);
      localStorage.setItem('token', newToken);
      setUser(userData);
      setUserType('user');
      
      return { success: true, token: newToken };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setUserType(null);
    localStorage.removeItem('token');
  };

  const updateSiteId = (id) => {
    setSiteId(id);
    localStorage.setItem('siteId', id);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{
      user,
      userType,
      token,
      loading,
      authLoading: loading,
      siteId,
      login,
      register,
      logout,
      updateSiteId,
      getAuthHeader,
      isAuthenticated: !!token
    }}>
      {children}
    </AuthContext.Provider>
  );
};
